package org.moxultimate.topwithmox.product.details

import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.launch
import org.moxultimate.topwithmox.core.base.ViewModel
import org.moxultimate.topwithmox.core.state.APIState
import org.moxultimate.topwithmox.product.api.ProductRepository
import org.moxultimate.topwithmox.product.data.ProductContentResponse

/**
 * ProductDetailState
 * Sealed hierarchy for the single product-content detail screen.
 */
sealed class ProductDetailState {
    /** Fetch in progress. */
    object Loading : ProductDetailState()

    /**
     * Fetch failed.
     *
     * @param error Human-readable error message.
     * @param statusCode HTTP status code returned by the server.
     */
    data class Error(val error: String, val statusCode: Int) : ProductDetailState()

    /**
     * Fetch succeeded.
     *
     * @param content Full product content entry with its products list.
     */
    data class Success(val content: ProductContentResponse) : ProductDetailState()
}

/**
 * ProductDetailViewModel
 * Fetches a single [ProductContentResponse] by ID and exposes it as [state].
 *
 * @param repository Data source for product content.
 */
class ProductDetailViewModel(
    private val repository: ProductRepository,
) : ViewModel() {

    private val _state = MutableStateFlow<ProductDetailState>(ProductDetailState.Loading)
    val state: StateFlow<ProductDetailState> = _state

    /**
     * loadContent
     * Triggers a GET /product-contents/{id} request and updates [state].
     *
     * @param contentId ID of the product content entry to fetch.
     */
    fun loadContent(contentId: Long) {
        viewModelScope.launch {
            repository.getProductContentById(contentId).collect { apiState ->
                _state.value = when (apiState) {
                    is APIState.Loading -> ProductDetailState.Loading
                    is APIState.Error   -> ProductDetailState.Error(apiState.error, apiState.statusCode)
                    is APIState.Success -> ProductDetailState.Success(apiState.data)
                }
            }
        }
    }
}
